import React, { FC, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useForm } from 'react-hook-form'
import { useAtom } from 'jotai'
import { Button } from '../../ui/Button/Button'
import { Typography } from '../../ui/Typography/Typography'
import { Member } from '../../../models/Member'
import { colors } from '../../../theme/palette'
import { FormInput, FormTextArea } from '../FormElements/FormInput'
import { FormControl } from '../../ui/FormElememts/FormControl'
import { baseButton } from '../../../theme/reuseables'
import { addNewNote } from '../../../api/notes'
import { CreateNewNoteType } from '../../../models/Note'
import { L, L_HTML } from '../../providers/Lokalise'
import { Workplace } from '../../../models/Workplace'
import { ImitatorNoAccessWarning } from '../Imitator/ImitatorNoAccessWarning'
import { profileAtom } from '../../../api/profile/profile'

interface EditProps {
    member?: Member
    workPlace?: Workplace
    type: 'member' | 'workplace'
    onUpdated?: () => void
    onClickCancel?: () => void
    hasNotes?: boolean
}

const useStyles = createUseStyles({
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: 'calc(100% - 100px)',
        paddingBottom: '80px',
    },
    accordionWrapper: {
        marginBottom: '20px',
    },
    select: {
        position: 'relative',
        height: '3.125rem',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.labelColor,
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

export type RegistrationFormFields = {
    Titel: string
    Indhold: string
    ReferenceType: string
}

export const AddNewNote: FC<EditProps> = ({
    member,
    type,
    workPlace,
    onUpdated,
    onClickCancel,
    hasNotes,
}) => {
    const [authProfile] = useAtom(profileAtom)
    const [showImitatorModal, setShowImitatorModal] = useState(false)
    const { register, handleSubmit, formState, reset } =
        useForm<RegistrationFormFields>()
    const classes = useStyles()
    const [isValid, setIsValid] = useState(false)

    const resetForm = (): void => {
        reset()
    }

    const { isDirty, dirtyFields } = formState

    const onSubmit = async (data: RegistrationFormFields): Promise<void> => {
        if (authProfile.isImitator) {
            setShowImitatorModal(true)
            return
        }
        const fieldsToEdit: CreateNewNoteType = {
            ReferenceId: type === 'member' ? member.memberId : workPlace.number,
            ReferenceType: type === 'member' ? 'MedlemIdent' : 'ArbejdsstedNr',
        }

        if (isDirty) {
            if (dirtyFields.Indhold) {
                fieldsToEdit.Indhold = data.Indhold
            }

            if (dirtyFields.Titel) {
                fieldsToEdit.Titel = data.Titel
            }

            const response = await addNewNote(fieldsToEdit)

            if (onUpdated && response) {
                onUpdated()
            }
        }
    }

    const checkIsValid = (): void => {
        // setIsValid(isDirty && dirtyFields.Indhold && dirtyFields.Indhold)
        setIsValid(isDirty)
    }

    useEffect(() => {
        checkIsValid()
    })

    return (
        <>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className={classes.contentWrapper}
            >
                <Typography
                    tag="h2"
                    size="XL"
                    align="center"
                    weight="bold"
                    style={{ margin: '20px 0 10px' }}
                >
                    {L_HTML('NOTER_NY-NOTE_FORM_TITEL')}
                </Typography>

                <FormControl>
                    <FormInput
                        label={L('NOTER_REDIGER_FORM_NOTE-OVERSKRIFT_LABEL')}
                        register={register}
                        name="Titel"
                        required
                        maxLength={100}
                    />
                </FormControl>

                <FormControl>
                    <FormTextArea
                        label={
                            hasNotes
                                ? 'Indhold'
                                : L('NOTER_NYNOTE_FORM_NOTE-INDHOLD_LABEL')
                        }
                        register={register}
                        name="Indhold"
                        maxCount={3000}
                    />
                </FormControl>
                <div className={classes.bottom}>
                    <Button
                        onClick={() => {
                            resetForm()
                            onClickCancel()
                        }}
                        type="secondary"
                        size="large"
                    >
                        {L('NOTER_REDIGER_FORM_ANNULLER_KNAP')}
                    </Button>
                    <Button
                        buttonType="submit"
                        type="primary"
                        size="large"
                        disabled={!isValid}
                    >
                        {L('NOTER_REDIGER_FORM_GEM_KNAP')}
                    </Button>
                </div>

                <div />
            </form>
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
        </>
    )
}

AddNewNote.defaultProps = {
    hasNotes: false,
}
